import { Immutable } from "immer";
import React, {
    type JSX,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import Modal from "react-modal";
import { BreakPoint } from "tsi-common-react/src/models/screen";
import { CSR_TOOLBAR_HEIGHT } from "tsi-common-react/src/styles/csr-toolbar";
import { isMobile } from "tsi-common-react/src/utils/detectMobile";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import DropDownCloseButton from "./DropdownCloseButton";
import DropdownLinkList from "./DropdownLinkList";
import { NavAdvert } from "./NavAdvert";
import {
    IMenuCategoryLink,
    IMenuItem,
    IMenuMobileTop,
    IMenuPageLink,
} from "./menu.interface";

import styles from "./index.module.scss";

const renderLink = (
    mobile: boolean,
    url: string,
    overrideTitle: string,
    linkID: number,
): JSX.Element => {
    const linkClassName = concatClassNames([
        styles.headerNavItem,
        mobile ? styles.headerNavItemMobile : null,
    ]);

    return (
        <a key={linkID} className={linkClassName} href={url}>
            {overrideTitle}{" "}
        </a>
    );
};

const renderCategoryLink = (
    linkObject: Immutable<IMenuCategoryLink>,
    linkID: number,
    onOpenModal: (event: React.MouseEvent<HTMLElement>) => void,
): JSX.Element => {
    return (
        <button
            key={linkID}
            onClick={(e) => {
                onOpenModal(e);
            }}
            className={styles.headerNavItem}
        >
            {linkObject.value.title}
        </button>
    );
};

const buildMenuLinks = (
    menuItems: ReadonlyArray<Immutable<IMenuItem>>,
    categoryItems: ReadonlyArray<Immutable<IMenuCategoryLink>>,
    mobile: boolean,
    isSubNav: boolean,
    modalIsOpen: boolean,
    onOpenModal: (event: React.MouseEvent<HTMLElement>) => void,
    onCloseModal: (
        event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => void,
): JSX.Element => {
    let index = 0;
    const menuClassName = concatClassNames([
        !isSubNav ? styles.headerPrimaryNav : null,
        modalIsOpen ? styles.isExpanded : null,
    ]);

    const buildPageLinks = (
        menuItem: Immutable<IMenuPageLink>,
    ): JSX.Element => {
        return renderLink(
            false,
            menuItem.value.page.url,
            menuItem.value.override_title,
            index++,
        );
    };

    const buildCategoryLinks = (
        menuItem: Immutable<IMenuCategoryLink>,
    ): JSX.Element[] => {
        const links: JSX.Element[] = [];

        // Build out Dropdown nav links (if they exist)
        const dropdownLinks = menuItem.value.sub_nav.filter((val) => {
            return (
                val.type === "page_link_with_image" ||
                val.type === "external_link" ||
                val.type === "sub_title" ||
                val.type === "relative_url"
            );
        });

        if (!isSubNav) {
            const link = renderCategoryLink(menuItem, index++, onOpenModal);
            links.push(link);
        }

        if (dropdownLinks.length <= 0) {
            return links;
        }

        if (isSubNav) {
            links.push(
                <DropdownLinkList
                    linksArray={dropdownLinks}
                    key={index++}
                    linkID={index++}
                    footerlinks={categoryItems}
                    closeModal={onCloseModal}
                />,
            );
            return links;
        }

        return links;
    };

    const buildMobileMenu = (
        menuItem: Immutable<IMenuMobileTop>,
    ): JSX.Element => {
        const url = menuItem.value.page.url || menuItem.value.external_link;

        return renderLink(true, url, menuItem.value.title, index++);
    };

    const menuLinks = menuItems.reduce(
        (memo, menuItem: Immutable<IMenuItem>) => {
            switch (menuItem.type) {
                case "external_link":
                case "mobile_top":
                    if (mobile) {
                        memo = memo.concat(
                            buildMobileMenu(
                                menuItem as Immutable<IMenuMobileTop>,
                            ),
                        );
                    }
                    break;

                case "page_link":
                    if (!isSubNav && !mobile) {
                        memo = memo.concat(buildPageLinks(menuItem));
                    }
                    break;

                case "nav_category":
                    if (!mobile) {
                        memo = memo.concat(buildCategoryLinks(menuItem));
                    }
                    break;

                default:
                    console.error(`Unknown menu item type: ${menuItem.type}`);
            }
            return memo;
        },
        [] as JSX.Element[],
    );

    return <nav className={menuClassName}>{menuLinks}</nav>;
};

type MainMenuProps = Immutable<{
    menuItems: IMenuItem[];
}>;

export const MainMenu: React.FC<MainMenuProps> = (props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isMobileNav, setIsMobileNav] = useState(false);
    const [initDropdownAni, setInitDropdownAni] = useState(false);
    const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());
    const isMobileNavRef = useRef(isMobileNav);
    const modalIsOpenRef = useRef(modalIsOpen);
    modalIsOpenRef.current = modalIsOpen;
    isMobileNavRef.current = isMobileNav;

    const setSafeTimeout = useCallback(
        (callback: () => void, delay: number) => {
            const timeoutId = setTimeout(() => {
                callback();
                timeoutRefs.current.delete(timeoutId);
            }, delay);
            timeoutRefs.current.add(timeoutId);
            return timeoutId;
        },
        [],
    );

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current.clear();
        };
    }, []);

    // Handle resize on client only — registered once, refs keep values current
    useEffect(() => {
        const win = getWindow();
        if (!win) return;

        const handleResize = () => {
            // Test for mobile view on android
            if (win.innerWidth < BreakPoint.SMALL && isMobile.Android()) {
                return;
            }
            if (modalIsOpenRef.current) {
                if (isMobileNavRef.current) {
                    setInitDropdownAni(false);
                }
                setSafeTimeout(
                    () => {
                        setModalIsOpen(false);
                        setIsMobileNav(false);
                    },
                    isMobileNavRef.current ? 250 : 0,
                );
            }
        };

        win.addEventListener("resize", handleResize);
        return () => {
            win.removeEventListener("resize", handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setSafeTimeout is stable (useCallback with [] deps)
    }, []);

    const handleCloseModal = useCallback(
        (
            event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
        ): void => {
            if (event) {
                event.preventDefault();
            }

            if (isMobileNavRef.current) {
                setInitDropdownAni(false);
            }

            setSafeTimeout(
                () => {
                    setModalIsOpen(false);
                    setIsMobileNav(false);
                },
                isMobileNavRef.current ? 250 : 0,
            );
        },
        [setSafeTimeout],
    );

    const handleOpenModal = useCallback(
        (event: React.MouseEvent<HTMLElement>): void => {
            event.preventDefault();

            if (event.currentTarget.id === "mobile-main-nav") {
                setIsMobileNav(true);

                setSafeTimeout(() => {
                    setInitDropdownAni(true);
                }, 10);
            }

            setModalIsOpen((prev) => !prev);
        },
        [setSafeTimeout],
    );

    const contentPosition: React.CSSProperties["position"] = isMobileNav
        ? "absolute"
        : "fixed";
    // 130px = $main-nav--height (header 90px + promo 40px); keep in sync with _layouts.scss.
    const contentTop: React.CSSProperties["top"] = `calc(130px + ${CSR_TOOLBAR_HEIGHT})`;
    const contentHeight: React.CSSProperties["height"] = "auto";
    const modalStyle: Modal.Styles = {
        overlay: {
            position: isMobileNav ? "absolute" : "fixed",
            top: !isMobileNav ? contentTop : 0,
            backgroundColor: !isMobileNav
                ? "rgba(0, 0, 0, 0.2)"
                : "rgba(0, 0, 0, 0)",
            zIndex: 100,
        },
        content: {
            position: contentPosition,
            top: !isMobileNav ? contentTop : 0,
            left: "0",
            right: "0",
            width: "100%",
            height: !isMobileNav ? contentHeight : "100%",
            overflow: "hidden",
            border: "none",
            padding: "0",
            borderTop: "1px solid #d8dcdd",
            backgroundColor: !isMobileNav
                ? "rgba(255, 255, 255, 1)"
                : "rgba(255, 255, 255, 0)",
            boxShadow: "0 10px 10px -5px rgba(0,0,0,.3)",
        },
    };

    // Filter once — used by both buildMenuLinks and navAdvert
    const categoryItems = props.menuItems.filter(
        (item): item is Immutable<IMenuCategoryLink> =>
            item.type === "nav_category",
    );

    const navAdvert = (): JSX.Element | undefined => {
        if (isMobileNav) {
            return;
        }
        return <NavAdvert menuItems={categoryItems} />;
    };

    // `header__dropdown-menu` stays literal: CMS nav-footer HTML ships its own
    // <style> that selects `.header__dropdown-menu a.button.button--secondary`.
    const dropdownMenuClassName = concatClassNames([
        styles.headerDropdownMenu,
        "header__dropdown-menu",
        initDropdownAni ? styles.headerDropdownMenuActive : null,
    ]);

    return (
        <div>
            {buildMenuLinks(
                props.menuItems,
                categoryItems,
                false,
                false,
                modalIsOpen,
                handleOpenModal,
                handleCloseModal,
            )}
            <Modal
                contentLabel={t`Shop Sealy menu`}
                ariaHideApp={false}
                aria={{ modal: true }}
                role="dialog"
                style={modalStyle}
                isOpen={modalIsOpen}
                onRequestClose={handleCloseModal}
                onAfterOpen={() => {
                    focusElement(`.${styles.headerDropdownClose}`);
                }}
            >
                <DropDownCloseButton
                    isMobileNav={isMobileNav}
                    closeModal={handleCloseModal}
                />

                <div className={dropdownMenuClassName}>
                    {buildMenuLinks(
                        props.menuItems,
                        categoryItems,
                        isMobileNav,
                        true,
                        modalIsOpen,
                        handleOpenModal,
                        handleCloseModal,
                    )}
                    {navAdvert()}
                </div>
            </Modal>
        </div>
    );
};
